import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate } from "react-router-dom"
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  CircleDashed,
  Clock,
  FileSearch,
  List,
  Loader2,
  Undo2,
  Wand2,
} from "lucide-react"

import { DataState } from "@/components/DataState"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { useAuth, useRbac } from "@/lib/auth"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import { PasoFormulario } from "@/routes/onboarding/PasoFormulario"
import {
  fetchPasos,
  posponerPaso,
  retomarPaso,
  type EstadoPaso,
  type PasoTraspaso,
} from "@/routes/onboarding/api"
import { ETIQUETA_ESTADO, textoDe } from "@/routes/onboarding/pasos"
import { t } from "@/lib/i18n"

/**
 * PRIMEROS PASOS — traer tu histórico, con Marty preguntando.
 *
 * Tres decisiones definen la pantalla. Las dos primeras vienen de la versión
 * anterior y siguen valiendo; la tercera es lo que cambió.
 *
 * 1. **Ningún paso se puede marcar a mano.** El estado se DERIVA de los datos
 *    que hay en la base. Una checklist manual mide lo que alguien recuerda haber
 *    hecho; ésta mide lo que el sistema puede comprobar — y en un traspaso, donde
 *    nadie sabe todavía qué se perdió por el camino, la primera da verde con la
 *    mitad de los datos dentro y nadie se entera hasta el primer cierre.
 *
 * 2. **No hay barra de progreso.** Un 71% sugiere que lo que falta es el 29% del
 *    trabajo, y aquí el último paso —cuadrar contra los totales del sistema
 *    anterior— es donde aparece la mitad de los problemas. Se cuentan pasos, que
 *    es un dato verdadero, en vez de estimar un avance que no lo es. El modo
 *    guiado dice «3 de 9», que sigue siendo un recuento y no una estimación.
 *
 * 3. **DOS MODOS, y el guiado es el primero que se ve.** Trece fichas abiertas a
 *    la vez son trece decisiones simultáneas para alguien que acaba de entrar:
 *    se lee todo, no se rellena nada y se cierra la pestaña. El modo guiado abre
 *    UNA pregunta, la deja rellenar ahí mismo y pasa a la siguiente. El modo
 *    lista sigue existiendo entero porque es lo que hace falta a la semana
 *    siguiente, cuando lo que se quiere no es avanzar sino ver dónde va todo.
 *
 * ---------------------------------------------------------------------------
 * CUÁL SE ABRE POR DEFECTO
 *
 * El guiado, **la primera vez y mientras quede algo pendiente**. No se infiere
 * «cliente nuevo» de cuántos pasos están listos: con el plan del P&L sembrado y
 * los períodos abiertos por otro, un cliente recién llegado ya tiene dos en
 * verde y saldría del cuestionario sin haberlo empezado. Lo que sí es un hecho
 * es si esta persona ya eligió un modo: si eligió lista, se respeta; si no ha
 * elegido nunca, es su primera vez y se le pregunta de una en una.
 *
 * La preferencia se guarda por CLIENTE (`tenantId`): quien lleva la contabilidad
 * de tres empresas está empezando con una y rodado con las otras dos.
 */

type Modo = "guiado" | "lista"

const CLAVE_MODO = "cfo.onboarding.modo."

function leerModo(tenantId: string | null): Modo | null {
  if (!tenantId) return null
  try {
    const v = window.localStorage.getItem(CLAVE_MODO + tenantId)
    return v === "guiado" || v === "lista" ? v : null
  } catch {
    // Sin almacenamiento se vuelve al guiado cada vez. Es aceptable: perder la
    // preferencia es un detalle, no pintar la pantalla sí sería un problema.
    return null
  }
}

function guardarModo(tenantId: string | null, modo: Modo): void {
  if (!tenantId) return
  try {
    window.localStorage.setItem(CLAVE_MODO + tenantId, modo)
  } catch {
    /* Ver arriba. */
  }
}

export function PrimerosPasosPage() {
  const { activeTenantId } = useAuth()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["onboarding-pasos", activeTenantId],
    queryFn: () => fetchPasos(),
  })

  const [modo, setModo] = React.useState<Modo | null>(() => leerModo(activeTenantId))

  // Al cambiar de cliente cambia la preferencia. Sin esto, elegir «lista» en una
  // empresa rodada se llevaría por delante el cuestionario de la que empieza.
  React.useEffect(() => {
    setModo(leerModo(activeTenantId))
  }, [activeTenantId])

  const elegir = (m: Modo) => {
    setModo(m)
    guardarModo(activeTenantId, m)
  }

  // Lo que el cuestionario recorre: ni lo que ya está, ni lo que se apartó.
  const porPreguntar = (data?.pasos ?? []).filter((p) => !p.pospuesto && p.estado !== "listo")
  const modoReal: Modo = modo ?? (porPreguntar.length > 0 ? "guiado" : "lista")

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Traspaso"
        title={t("Primeros pasos")}
        /*
          SIN DESCRIPCIÓN, y es deliberado. El layout ya monta la nota de Marty
          con la apertura de la sección (`lib/marty.ts`), que dice exactamente
          esto. Dos párrafos idénticos uno encima de otro es el tipo de texto de
          más que Cesar pidió quitar, y además pondría dos filetes rojos en la
          misma pieza: el de la nota y el punto del titular.
        */
        actions={
          data ? (
            <div className="flex items-center rounded-md border bg-surface p-0.5">
              <BotonModo
                activo={modoReal === "guiado"}
                onClick={() => elegir("guiado")}
                icono={Wand2}
                disabled={porPreguntar.length === 0}
              >
                {t("Uno a uno")}</BotonModo>
              <BotonModo activo={modoReal === "lista"} onClick={() => elegir("lista")} icono={List}>
                {t("Ver todo")}</BotonModo>
            </div>
          ) : null
        }
      />

      <DataState
        loading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        empty={false}
        rows={4}
        columns={[100]}
      >
        {data ? (
          <>
            {modoReal === "guiado" ? (
              <Cuestionario
                pasos={porPreguntar}
                listos={data.listos}
                total={data.total}
                onTerminar={() => elegir("lista")}
              />
            ) : (
              <Recuento
                listos={data.listos}
                total={data.total}
                pospuestos={data.pospuestos}
                pendientes={data.pendientes_activos}
              />
            )}

            {modoReal === "lista" ? (
              <>
                <Lista pasos={data.pasos} />
                {/*
                  AL FINAL DE LA LISTA, que es donde Cesar lo pidió: «que se
                  active al final de ver todo». Aquí se ven los trece de golpe,
                  así que quien pulsa sabe qué deja sin hacer.
                */}
                <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-3">
                  <BotonFinalizar quedan={data.pendientes_activos} />
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </DataState>
    </div>
  )
}

/**
 * FINALIZAR EL TRASPASO.
 *
 * Cesar: «debería de haber un botón de Finalizar onboarding (que se active al
 * final de ver todo o a partir de la 10 y 11 si es vista uno a uno)».
 *
 * ===========================================================================
 * POR QUÉ NO ESTABA, Y POR QUÉ HACÍA FALTA
 * ===========================================================================
 *
 * El traspaso terminaba SOLO: cuando no quedaba ningún paso pendiente, la
 * pantalla cambiaba a «ya está todo lo que te podía pedir». Eso deja fuera el
 * caso normal — alguien que ya ha dado lo que tenía y cuyos últimos pasos son
 * revisiones que va a hacer con el sistema en marcha. Ése se quedaba dentro del
 * cuestionario sin una puerta, que es la otra mitad del punto 4: el traspaso no
 * te puede echar fuera, y tampoco te puede retener.
 *
 * ---------------------------------------------------------------------------
 * CUÁNDO SE ACTIVA, Y POR QUÉ ESE UMBRAL
 *
 * En la LISTA, siempre: ahí se ven los trece de golpe, así que quien pulsa sabe
 * exactamente qué deja sin hacer.
 *
 * En el cuestionario, **a partir de la pregunta 10**. Es el umbral que pidió
 * Cesar y tiene su razón: las últimas preguntas son las de REVISAR —la cola de
 * comprobantes, el asiento de apertura, el cuadre— y ésas se hacen con el
 * sistema andando, no antes de entrar. Ofrecerlo desde la primera convertiría
 * el cuestionario en opcional justo para quien más lo necesita.
 *
 * ---------------------------------------------------------------------------
 * NO MARCA NADA COMO HECHO, Y ESO ES LO IMPORTANTE
 *
 * Finalizar es salir, no dar por bueno. Los pasos que queden siguen pendientes
 * y la pantalla sigue aquí para volver — un botón que además los marcara como
 * listos convertiría «lo veo después» en «está comprobado», y el estado de cada
 * paso se DERIVA de los datos justamente para que nadie pueda afirmarlo a mano.
 *
 * Lo que sí hace es llevar a la guía, que es lo que Cesar pidió a cambio en el
 * punto 4: terminar y que te enseñen la plataforma.
 */
function BotonFinalizar({
  quedan, className,
}: {
  /** Pasos que se dejan sin hacer. Se dicen: salir a ciegas no es salir. */
  quedan: number
  className?: string
}) {
  const navegar = useNavigate()
  const [confirmando, setConfirmando] = React.useState(false)

  const finalizar = () => {
    sessionStorage.setItem("marty.guia.lanzar", "1")
    navegar("/dashboard")
  }

  if (quedan === 0) {
    return (
      <Button size="sm" className={className} onClick={finalizar}>
        {t("Finalizar el traspaso")}<ArrowRight />
      </Button>
    )
  }

  if (!confirmando) {
    return (
      <Button size="sm" variant="outline" className={className}
              onClick={() => setConfirmando(true)}>
        {t("Finalizar el traspaso")}<ArrowRight />
      </Button>
    )
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {/*
        SE DICE CUÁNTOS QUEDAN Y QUE NO SE PIERDEN. El miedo a pulsar aquí es
        «¿y si lo que falta se da por hecho?» — y la respuesta es que no, así
        que vale más decirla que dejar que cada uno la suponga.
      */}
      <span className="text-2xs leading-5 text-muted-foreground">
        {t("Te quedan")} <strong className="font-medium">{quedan}</strong>{" "}
        {t("sin hacer. No se dan por hechos: los dejo pendientes y puedes volver cuando quieras.")}
      </span>
      <Button size="xs" onClick={finalizar}>
        {t("Finalizar igual")}
      </Button>
      <Button size="xs" variant="ghost" onClick={() => setConfirmando(false)}>
        {t("Sigo")}
      </Button>
    </div>
  )
}

function BotonModo({
  activo,
  onClick,
  icono: Icono,
  disabled,
  children,
}: {
  activo: boolean
  onClick: () => void
  icono: typeof Wand2
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={activo}
      className={cn(
        "inline-flex h-6 items-center gap-1.5 rounded-sm px-2 text-2xs font-medium transition-colors",
        activo
          ? "bg-brand text-brand-foreground"
          : "text-muted-foreground hover:bg-accent hover:text-foreground",
        disabled && "cursor-not-allowed opacity-40 hover:bg-transparent",
      )}
    >
      <Icono className="h-3 w-3" aria-hidden="true" />
      {children}
    </button>
  )
}

/**
 * El recuento, y SOLO el recuento.
 *
 * Antes arrastraba detrás la `nota` del backend —«cada paso se deriva de los
 * datos, no de una marca manual…»—, que es una explicación de cómo está hecho
 * el producto puesta donde se espera un dato del cliente. Esa idea sigue
 * estando: la dice el propio paso, en verde, cuando está comprobado.
 */
/**
 * EL RECUENTO, con un anillo de SEGMENTOS y no una barra de porcentaje.
 *
 * La decisión 2 de arriba sigue en pie: no hay barra de progreso porque un 71 %
 * sugiere que lo que falta es el 29 % del trabajo. Un anillo con un segmento
 * por paso NO es eso: es el mismo «3 de 9» dibujado, un recuento y no una
 * estimación. Cesar, 2026-10-04: «algo de gamificación» — esto es lo que cabe:
 * ver que quedan tres huecos y que uno se rellena al contestar.
 */
function Recuento({
  listos,
  total,
  pospuestos,
  pendientes,
}: {
  listos: number
  total: number
  pospuestos: number
  pendientes: number
}) {
  return (
    <div className="flex items-center gap-3">
      <AnilloDePasos listos={listos} total={total} pospuestos={pospuestos} />
      <p className="text-2xs leading-5 text-muted-foreground">
        <span className="num-strong">{listos}</span> de {total} comprobados
        {pendientes > 0 ? (
          <>
            {" "}· <span className="num-strong">{pendientes}</span> {" "}{t("esperándote")}</>
        ) : null}
        {pospuestos > 0 ? (
          <>
            {" "}· <span className="num-strong">{pospuestos}</span> {" "}{t("para después")}</>
        ) : null}
        .
      </p>
    </div>
  )
}

function AnilloDePasos({ listos, total, pospuestos }: { listos: number; total: number; pospuestos: number }) {
  if (total <= 0) return null
  const r = 15
  const largo = 2 * Math.PI * r
  const hueco = total > 1 ? 2.5 : 0
  const tramo = largo / total - hueco
  return (
    <svg viewBox="0 0 40 40" className="h-10 w-10 shrink-0 -rotate-90" role="img"
         aria-label={`${listos} ${t("de")} ${total}`}>
      {Array.from({ length: total }).map((_, i) => {
        const listo = i < listos
        const pospuesto = !listo && i >= total - pospuestos
        return (
          <circle
            key={i} cx="20" cy="20" r={r} fill="none" strokeWidth="3.5"
            stroke={listo ? "hsl(var(--chart-1))" : pospuesto ? "hsl(var(--border-strong))" : "hsl(var(--border))"}
            strokeDasharray={`${tramo} ${largo - tramo}`}
            strokeDashoffset={-(i * (tramo + hueco))}
            style={{ transition: "stroke 300ms" }}
          />
        )
      })}
    </svg>
  )
}

/* ==========================================================================
   EL CUESTIONARIO — una pregunta, y la siguiente

   El índice vive AQUÍ y no en la URL a propósito: al rellenar un paso el
   backend lo devuelve `listo` y desaparece de la lista de preguntas, así que la
   posición N deja de significar lo mismo entre una respuesta y la siguiente. Un
   índice en la URL sobreviviría a esa recolocación apuntando a otra pregunta.
   ========================================================================== */

/**
 * Desde qué pregunta se ofrece finalizar. Cesar: «a partir de la 10 y 11 si es
 * vista uno a uno».
 *
 * Se declara aquí y no se escribe en el `if` para que el número tenga nombre:
 * el día que el traspaso gane o pierda un paso, alguien va a tener que decidir
 * si el umbral se mueve, y un `10` suelto dentro de una condición no invita a
 * esa pregunta.
 */
//: Con NUEVE pasos (2026-10-02), desde la séptima: las dos últimas —la
//: apertura y el cierre— son las que más gente deja para después.
const FINALIZAR_DESDE = 7

function Cuestionario({
  pasos,
  listos,
  total,
  onTerminar,
}: {
  pasos: PasoTraspaso[]
  listos: number
  total: number
  onTerminar: () => void
}) {
  const navegar = useNavigate()
  const [i, setI] = React.useState(0)

  // Al contestar una pregunta la lista se acorta. Si el índice se quedara fuera
  // de rango la pantalla se pintaría vacía sin decir por qué.
  const indice = Math.min(i, Math.max(pasos.length - 1, 0))
  React.useEffect(() => {
    if (i !== indice) setI(indice)
  }, [i, indice])

  if (pasos.length === 0) {
    return (
      <section className="rounded-panel border bg-surface p-5 text-center shadow-xs">
        <p className="titular punto text-md">{t("Ya está todo lo que te podía pedir")}</p>
        <p className="mx-auto mt-1.5 max-w-[48ch] text-xs leading-5 text-muted-foreground">
          {t("No me queda nada por preguntarte. De aquí en adelante los números son mi problema: cuando algo no cuadre, te aviso yo.")}</p>
        {/*
          AL TERMINAR, LA GUÍA. Cesar, punto 4 de la cuarta tanda: «deberías de
          finalizar onboarding o skipearlo y ya recién ahí que te haga una guía y
          te lleve por cada parte». Ésta es la puerta: el botón principal ya no
          es «ver el dashboard» —que te deja solo delante de una pantalla— sino
          el recorrido.

          Se pide por `sessionStorage` y no por una prop porque la guía vive en
          `AppLayout`, que está por ENCIMA de esta pantalla: pasarle un callback
          habría obligado a subir el estado tres niveles para un clic.
        */}
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          <Button
            size="sm"
            onClick={() => {
              sessionStorage.setItem("marty.guia.lanzar", "1")
              navegar("/dashboard")
            }}
          >
            {t("Enséñame la plataforma")}<ArrowRight />
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to="/dashboard">
              {t("Ver cómo va el mes")}
            </Link>
          </Button>
          <Button size="sm" variant="ghost" onClick={onTerminar}>
            <List />
            {t("Repasar los pasos")}</Button>
        </div>
      </section>
    )
  }

  const paso = pasos[indice]

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <p className="eyebrow">
          {t("Pregunta")}{" "}{indice + 1} de {pasos.length}
        </p>
        {/*
          El recorrido, punto a punto. NO es una barra de progreso: son tantos
          puntos como preguntas quedan, y un punto no sugiere que el trabajo que
          falta sea proporcional a lo que queda sin pintar.
        */}
        <ol className="flex items-center gap-1" aria-hidden="true">
          {pasos.map((p, n) => (
            <li
              key={p.clave}
              className={cn(
                "h-1 rounded-full transition-all",
                n === indice ? "w-5 bg-brand" : "w-1.5 bg-border-strong",
              )}
            />
          ))}
        </ol>
        {/*
          Los DOS denominadores, juntos y dichos aparte. «Pregunta 1 de 7» y
          «6 de 13 comprobados» en dos líneas seguidas, cada una con su número,
          se leen como una contradicción: siete son las que quedan por preguntar
          y trece los pasos del traspaso.
        */}
        <p className="ml-auto text-2xs text-muted-foreground">
          <span className="num-strong">{listos}</span> de {total}{" "}{t("ya comprobados")}</p>
      </div>

      <FichaPaso paso={paso} numero={indice + 1} destacada />

      <div className="flex flex-wrap items-center justify-between gap-2 pt-0.5">
        <Button
          size="sm"
          variant="ghost"
          disabled={indice === 0}
          onClick={() => setI((n) => Math.max(n - 1, 0))}
        >
          <ArrowLeft />
          {t("Anterior")}</Button>
        <div className="flex flex-wrap items-center gap-2">
          {/*
            FINALIZAR, A PARTIR DE LA PREGUNTA 10. Es el umbral que pidió Cesar
            y tiene su razón: de ahí en adelante las preguntas son las de
            REVISAR —la cola, el asiento de apertura, el cuadre— y ésas se hacen
            con el sistema andando. Ofrecerlo antes convertiría el cuestionario
            en opcional justo para quien más lo necesita.
          */}
          {indice + 1 >= FINALIZAR_DESDE ? (
            <BotonFinalizar quedan={pasos.length} />
          ) : null}
          {indice < pasos.length - 1 ? (
            <Button size="sm" variant="outline" onClick={() => setI((n) => n + 1)}>
              {t("Siguiente")}<ArrowRight />
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={onTerminar}>
              <List />
              {t("Ver todo lo que queda")}</Button>
          )}
        </div>
      </div>
    </section>
  )
}

/* ==========================================================================
   LA LISTA — todo a la vez, plegado

   Lo que era una ficha de cinco párrafos ahora es una fila de dos líneas que se
   despliega. Sigue estando todo; lo que cambió es que ya no hay que leerlo para
   saber dónde va el traspaso.
   ========================================================================== */

function Lista({ pasos }: { pasos: PasoTraspaso[] }) {
  const activos = pasos.filter((p) => !p.pospuesto)
  const apartados = pasos.filter((p) => p.pospuesto)

  return (
    <>
      <ol className="mt-1 space-y-1.5">
        {activos.map((paso) => (
          <FichaPaso key={paso.clave} paso={paso} numero={pasos.indexOf(paso) + 1} plegable />
        ))}
      </ol>

      {/*
        LO POSPUESTO SE APARTA, NO SE ESCONDE. Va debajo y en gris: sacar un paso
        del camino crítico es lo que se pidió, pero dejar de verlo lo convertiría
        en un olvido con aspecto de decisión.
      */}
      {apartados.length > 0 ? (
        <section className="mt-4">
          <h2 className="eyebrow">{t("Para después")}</h2>
          <p className="mt-0.5 max-w-[68ch] text-2xs leading-5 text-muted-foreground">
            {t("Siguen pendientes: apartarlos no los da por hechos. Vuelven arriba en cuanto los retomes.")}</p>
          <ol className="mt-1.5 space-y-1.5 opacity-70">
            {apartados.map((paso) => (
              <FichaPaso key={paso.clave} paso={paso} numero={pasos.indexOf(paso) + 1} plegable />
            ))}
          </ol>
        </section>
      ) : null}
    </>
  )
}

/* ========================================================================== */

const ICONO: Record<EstadoPaso, typeof Check> = {
  listo: Check,
  en_curso: Loader2,
  atencion: AlertTriangle,
  pendiente: CircleDashed,
}

const TONO: Record<EstadoPaso, string> = {
  listo: "border-success/30 bg-success-soft text-success",
  en_curso: "border-brand/30 bg-brand-soft text-brand",
  atencion: "border-warning/40 bg-warning-soft text-warning",
  pendiente: "border-border bg-surface-sunken text-muted-foreground",
}

/**
 * LA FICHA DE UN PASO, en sus dos formas.
 *
 * `destacada` es la del cuestionario: abierta, con el formulario a la vista.
 * `plegable` es la de la lista: cerrada, y se abre al pulsarla.
 *
 * Es el MISMO componente en los dos modos y no dos parecidos. Dos fichas
 * distintas divergen en cuanto alguien toque una: el modo guiado acabaría
 * teniendo el formulario nuevo y la lista no, y nadie lo notaría hasta que un
 * cliente rellene el traspaso desde el modo equivocado.
 */
function FichaPaso({
  paso,
  numero,
  destacada,
  plegable,
}: {
  paso: PasoTraspaso
  numero: number
  destacada?: boolean
  plegable?: boolean
}) {
  const texto = textoDe(paso)
  const Icono = ICONO[paso.estado]
  const { can } = useRbac()
  const puedeEditar = can("edit")
  const [abierta, setAbierta] = React.useState(!plegable)
  const [verPorque, setVerPorque] = React.useState(false)

  const cabecera = (
    <>
      <span
        className={cn(
          "grid h-6 w-6 shrink-0 place-items-center rounded-full border text-2xs font-semibold",
          TONO[paso.estado],
        )}
        aria-hidden="true"
      >
        {paso.estado === "listo" ? <Icono className="h-3 w-3" /> : numero}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h3 className={cn("font-semibold", destacada ? "titular text-md" : "text-sm")}>
            {texto.titulo}
          </h3>
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-sm border px-1.5 py-px text-2xs font-medium",
              TONO[paso.estado],
            )}
          >
            <Icono
              className={cn("h-2.5 w-2.5", paso.estado === "en_curso" && "animate-spin")}
              aria-hidden="true"
            />
            {ETIQUETA_ESTADO[paso.estado]}
          </span>
        </div>
        <p
          className={cn(
            "mt-0.5 text-2xs leading-5 text-muted-foreground",
            plegable && !abierta && "clamp-2",
          )}
        >
          {texto.marty}
        </p>
      </div>
    </>
  )

  return (
    <li
      className={cn(
        "list-none rounded-panel border bg-surface transition-colors",
        destacada && "shadow-xs",
        plegable && "hover:border-border-strong",
      )}
    >
      {plegable ? (
        /*
          La cabecera entera es el disparador, no un chevron de doce píxeles: en
          una lista de trece filas apuntar al chevron es lo que hace que nadie
          las abra. `aria-expanded` va en el botón porque es lo que anuncia el
          lector de pantalla al llegar.
        */
        <button
          type="button"
          onClick={() => setAbierta((v) => !v)}
          aria-expanded={abierta}
          className={cn(
            "flex w-full flex-wrap items-start gap-3 rounded-panel px-3 py-2 text-left",
            "transition-colors hover:bg-row-hover",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            abierta && "rounded-b-none border-b bg-surface-sunken/50",
          )}
        >
          {cabecera}
          <ChevronDown
            className={cn(
              "mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
              abierta && "rotate-180",
            )}
            aria-hidden="true"
          />
        </button>
      ) : (
        <div className="flex flex-wrap items-start gap-3 border-b bg-surface-sunken/50 px-3 py-2">
          {cabecera}
        </div>
      )}

      {abierta ? (
        <div className="space-y-2.5 px-3 py-2.5">
          <p className="max-w-[72ch] text-xs leading-5">{texto.que}</p>

          {/*
            DÓNDE ENCONTRARLO, y va AQUÍ y no dentro del plegado.

            Cesar: «el por qué te lo pido no queda claro ya que estás poniendo
            dónde encontrarlo».

            Y era literal: el plegado «Por qué te lo pido» abría DOS párrafos —
            `como` primero y `porque` después. `como` dice de qué documento sacar
            el dato («de la ficha RUC, no de una factura vieja»); `porque` dice
            por qué este paso va donde va. Lo primero que se leía al abrir el
            porqué era una instrucción de archivo, así que la pregunta quedaba
            sin responder aunque la respuesta estuviera justo debajo.

            Son dos cosas distintas y ahora van en dos sitios distintos: el DÓNDE
            es instrucción y se ve siempre, junto a lo que hay que rellenar; el
            POR QUÉ es fundamento y sigue plegado.
          */}
          {texto.como ? (
            <p className="flex max-w-[72ch] gap-1.5 text-2xs leading-5 text-muted-foreground">
              <FileSearch className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
              <span>
                <span className="font-medium text-foreground">{t("Dónde encontrarlo:")}{" "}</span>
                {texto.como}
              </span>
            </p>
          ) : null}

          {/*
            LO QUE EL SISTEMA VE AHORA MISMO. Visualmente distinto del resto: lo
            de arriba es la instrucción, que no cambia; esto es el estado de este
            cliente hoy, y confundirlos haría que la instrucción pareciera un
            reproche.
          */}
          {paso.detalle ? (
            <p
              className={cn(
                "rounded-md px-2.5 py-2 text-2xs leading-5",
                paso.estado === "atencion"
                  ? "border border-warning/40 bg-warning-soft"
                  : "bg-surface-sunken text-muted-foreground",
              )}
            >
              <span className="font-medium">{t("Ahora mismo:")}{" "}</span>
              {paso.detalle}
            </p>
          ) : null}

          {paso.estado === "listo" ? (
            <p className="text-2xs text-success">
              {t("Comprobado sobre tus datos, no marcado a mano.")}</p>
          ) : null}

          {/*
            EL FORMULARIO SE QUEDA AUNQUE EL PASO ESTÉ LISTO.

            Antes se escondía con `paso.estado !== "listo"`, y para un paso que
            pregunta UNA cosa —¿cuál es tu identificador fiscal?— eso es correcto:
            contestada, la pregunta sobra.

            Pero los pasos que Cesar pidió reconstruir no preguntan una cosa:
            piden DATOS QUE SIGUEN LLEGANDO. «Tu gente y tus contrapartes» se pone
            en verde con la primera persona cargada, y con el formulario
            escondido el botón de «añadir una persona» —que es literalmente el
            punto 16— dejaba de existir justo después de usarlo una vez. Lo mismo
            con la facturación (punto 19) y con los bancos (17).

            Que un paso esté listo significa «ya me diste bastante para seguir»,
            no «no me puedes dar más». El aviso de comprobado se queda encima, que
            es lo que distingue las dos cosas.
          */}
          {texto.formulario ? <PasoFormulario formulario={texto.formulario} /> : null}

          {paso.pospuesto ? (
            <Apartado paso={paso} titulo={texto.titulo} puedeEditar={puedeEditar} />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {/*
                EN EL CUESTIONARIO EL ENLACE DEJA DE SER EL BOTÓN PRINCIPAL.

                Cesar, punto 4 de la cuarta tanda: «el punto 9, 10 y 11 tienen
                botones que te llevan al otro lado… te bota de la página de
                onboarding, deberías de finalizar onboarding o skipearlo y ya
                recién ahí que te haga una guía».

                Tiene razón y el defecto era de JERARQUÍA, no del enlace. Pintado
                como botón primario, «Ir a la cola» se lee como «esto es lo que
                toca ahora» — y lo que toca ahora es terminar de contestar. Al
                pulsarlo te vas, y nada te trae de vuelta: quedan preguntas sin
                hacer y nadie lo sabe.

                En el cuestionario (`destacada`) pasa a ser un enlace discreto
                que AVISA de que sale del traspaso; «Siguiente» se queda como la
                acción principal. En la LISTA sigue siendo un botón: ahí ya se
                está repasando, no contestando, y salir es lo esperado.
              */}
              {paso.estado !== "listo"
                ? [...(texto.accion ? [texto.accion] : []), ...(texto.acciones ?? [])].map((accion) =>
                  destacada ? (
                    <Button key={accion.a} asChild size="sm" variant="ghost">
                      <Link to={accion.a}>
                        {accion.etiqueta}
                        <span className="text-muted-foreground">
                          {" · "}{t("sales del traspaso")}
                        </span>
                        <ArrowRight />
                      </Link>
                    </Button>
                  ) : (
                    <Button key={accion.a} asChild size="sm">
                      <Link to={accion.a}>
                        {accion.etiqueta}
                        <ArrowRight />
                      </Link>
                    </Button>
                  ))
                : null}
              {/*
                «ESTO LO VEMOS DESPUÉS» NO VA EN EL CUESTIONARIO.

                Cesar: «el botón de esto lo vemos después elimínalo porque ya lo
                tienes en siguiente abajo».

                En el modo guiado la ficha lleva debajo «Anterior / Siguiente», y
                «Siguiente» ES saltarse esta pregunta. Dos botones para pasar de
                largo, uno al lado del otro, obligan a decidir en qué se
                diferencian antes de poder seguir — y la respuesta («uno lo
                apunta, el otro no») no la puede adivinar nadie mirándolos.

                En la LISTA sí se queda, y no es una excepción caprichosa: ahí no
                hay «Siguiente» ninguno, así que sin este botón no habría forma
                de apartar un paso. Es justo el sitio donde se aparta de verdad —
                mirando los trece a la vez— y no de paso, contestando.
              */}
              {puedeEditar && paso.estado !== "listo" && !destacada ? (
                <Posponer clave={paso.clave} titulo={texto.titulo} aviso={texto.siSePospone} />
              ) : null}
            </div>
          )}

          {/*
            EL FUNDAMENTO SE PLIEGA, NO SE BORRA. Cesar pidió menos texto y tenía
            razón; pero casi todo lo que hay en `porque` se aprendió cargando
            cuatro países de verdad y equivocándose. Plegado, la ficha se lee en
            cuatro líneas y quien quiera el porqué lo tiene a un clic.
          */}
          {/* Solo `porque`: el `como` ya se ve arriba, que es de lo que se quejó Cesar. */}
          {texto.porque ? (
            <div className="border-t pt-2">
              <button
                type="button"
                onClick={() => setVerPorque((v) => !v)}
                aria-expanded={verPorque}
                className={cn(
                  "inline-flex items-center gap-1 rounded-sm px-1 py-0.5 -ml-1 text-2xs font-medium",
                  "text-muted-foreground transition-colors",
                  "hover:bg-accent hover:text-foreground",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                )}
              >
                <ChevronDown
                  className={cn("h-3 w-3 transition-transform", verPorque && "rotate-180")}
                  aria-hidden="true"
                />
                {verPorque ? "Ocultar el porqué" : "Por qué te lo pido"}
              </button>
              {verPorque ? (
                <div className="animate-row-in mt-1.5 border-l-2 border-border-strong pl-2.5">
                  <p className="max-w-[72ch] text-2xs leading-5 text-muted-foreground">
                    {texto.porque}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

function Apartado({
  paso,
  titulo,
  puedeEditar,
}: {
  paso: PasoTraspaso
  titulo: string
  puedeEditar: boolean
}) {
  const { toast } = useToast()
  const qc = useQueryClient()

  const retomar = useMutation({
    mutationFn: () => retomarPaso(paso.clave),
    onSuccess: () => {
      toast({ title: `«${titulo}» vuelve al camino` })
      void qc.invalidateQueries({ queryKey: ["onboarding-pasos"] })
    },
    onError: onWriteError("el paso", "retomar"),
  })

  return (
    <div className="rounded-md border border-dashed px-2.5 py-2">
      <p className="text-2xs leading-5 text-muted-foreground">
        <Clock className="mr-1 inline h-3 w-3" aria-hidden="true" />
        <span className="font-medium">{t("Apartado")}</span>
        {paso.pospuesto?.por ? ` por ${paso.pospuesto.por}` : ""}: «{paso.pospuesto?.motivo}».{" "}
        <strong className="font-medium">{t("Sigue pendiente")}</strong> {" "}{t("— apartarlo no lo da por hecho.")}</p>
      {puedeEditar ? (
        <Button
          size="xs"
          variant="outline"
          className="mt-1.5"
          disabled={retomar.isPending}
          onClick={() => retomar.mutate()}
        >
          <Undo2 />
          {t("Retomarlo ahora")}</Button>
      ) : null}
    </div>
  )
}

function Posponer({
  clave,
  titulo,
  aviso,
}: {
  clave: string
  titulo: string
  aviso?: string
}) {
  const [abierto, setAbierto] = React.useState(false)
  const [motivo, setMotivo] = React.useState("")
  const { toast } = useToast()
  const qc = useQueryClient()

  const posponer = useMutation({
    mutationFn: () => posponerPaso(clave, motivo),
    onSuccess: (r) => {
      toast({ title: `«${titulo}» queda para después`, description: r.aviso })
      setAbierto(false)
      setMotivo("")
      void qc.invalidateQueries({ queryKey: ["onboarding-pasos"] })
    },
    onError: onWriteError("el paso", "posponer"),
  })

  if (!abierto) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setAbierto(true)}>
        {t("Esto lo vemos después")}</Button>
    )
  }

  return (
    <div className="animate-row-in w-full space-y-1.5 rounded-md border bg-surface-sunken/50 px-2.5 py-2">
      {/*
        QUÉ PASA SI SE POSPONE, ANTES de posponerlo. Posponer los saldos de
        apertura y posponer la carga de personas tienen consecuencias muy
        distintas, y el botón se llama igual en los dos casos.
      */}
      {aviso ? (
        <p className="text-2xs leading-5 text-warning">
          <AlertTriangle className="mr-1 inline h-3 w-3" aria-hidden="true" />
          {aviso}
        </p>
      ) : null}
      <Textarea
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        rows={2}
        className="text-xs"
        placeholder={t("Por qué se deja para después. Queda con tu nombre y la fecha.")}
        aria-label={t("Motivo")}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={motivo.trim().length < 3 || posponer.isPending}
          onClick={() => posponer.mutate()}
        >
          <Clock />
          {posponer.isPending ? "Guardando…" : "Dejarlo para después"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setAbierto(false)}>
          {t("Cancelar")}</Button>
      </div>
    </div>
  )
}
